import type {
  GovernanceState,
  GovernGuidanceKey,
  GovernSourceBlocker,
} from "../../enums/govern";
import { GOVERN_GUIDANCE_KEY, GOVERNANCE_STATE } from "../../enums/govern";
import type { DeliveryBrief } from "../brief/briefTypes";
import {
  missingVerificationRoles,
  sourceBriefReady,
  verificationSummary,
} from "./governStatus";
import type { GovernWorkItem } from "./governTypes";

type GovernanceNextAction = {
  key: GovernGuidanceKey;
  action: string;
  message: string;
};

export function governanceNextAction(
  work: GovernWorkItem,
  brief: DeliveryBrief | null,
): GovernanceNextAction {
  if (!brief) {
    return sourceAction(
      GOVERN_GUIDANCE_KEY.SOURCE_UNAVAILABLE,
      "Source Brief is unavailable. Open Brief before verification can continue.",
    );
  }
  if (!sourceBriefReady(brief)) {
    return sourceAction(
      GOVERN_GUIDANCE_KEY.SOURCE_NOT_READY,
      "Source Brief is no longer Ready for delivery. Resolve it in Brief before verification can continue.",
    );
  }
  const lifecycleAction = lifecycleNextAction(work.state);
  if (lifecycleAction) return lifecycleAction;
  if (work.checks.length === 0) {
    return {
      key: GOVERN_GUIDANCE_KEY.NO_CHECKS,
      action: "Open Brief",
      message: "No acceptance criteria are available to verify.",
    };
  }
  const missingRoles = missingVerificationRoles(work);
  if (missingRoles.length > 0) {
    return {
      key: GOVERN_GUIDANCE_KEY.ASSIGN_ROLES,
      action: "Assign roles",
      message: `Assign ${joinLabels(missingRoles)} before completing verification.`,
    };
  }
  const summary = verificationSummary(work);
  if (summary.failed > 0) {
    return {
      key: GOVERN_GUIDANCE_KEY.SEND_BACK_FOR_REWORK,
      action: "Send back for rework",
      message: `${summary.failed} failed check${summary.failed === 1 ? " requires" : "s require"} rework.`,
    };
  }
  if (summary.unchecked > 0) {
    return {
      key: GOVERN_GUIDANCE_KEY.COMPLETE_CHECKS,
      action: "Complete verification",
      message: `${summary.unchecked} required check${summary.unchecked === 1 ? " has" : "s have"} not been completed.`,
    };
  }
  if (summary.invalid > 0) {
    return {
      key: GOVERN_GUIDANCE_KEY.COMPLETE_EVIDENCE,
      action: "Complete verification",
      message: `${summary.invalid} check${summary.invalid === 1 ? " still needs" : "s still need"} evidence or a responsible verifier.`,
    };
  }
  return {
    key: GOVERN_GUIDANCE_KEY.VERIFY_COMPLETE,
    action: "Verify complete",
    message: "All required checks passed. Verification can be completed.",
  };
}

export function verificationEditingMessage(
  work: GovernWorkItem,
  brief: DeliveryBrief | null,
): string | null {
  if (!brief)
    return "Verification is unavailable while the source Brief is unavailable.";
  if (!sourceBriefReady(brief)) {
    return "Verification is unavailable until the source Brief is Ready for delivery.";
  }
  if (
    work.state === GOVERNANCE_STATE.READY_FOR_DELIVERY ||
    work.state === GOVERNANCE_STATE.IN_DELIVERY
  ) {
    return "Verification becomes available when the work is Ready for review.";
  }
  if (work.state === GOVERNANCE_STATE.NEEDS_REWORK) {
    return "Verification results are retained while rework is in progress.";
  }
  if (
    work.state === GOVERNANCE_STATE.VERIFIED ||
    work.state === GOVERNANCE_STATE.REVIEW_SCHEDULED
  ) {
    return "Verification evidence is locked after completion.";
  }
  return null;
}

export function verificationSummaryMessage(work: GovernWorkItem): string {
  const summary = verificationSummary(work);
  if (summary.total === 0) return "No verification checks are available.";
  const failed = `${summary.failed} check${summary.failed === 1 ? "" : "s"} failed.`;
  if (summary.invalid > 0) {
    const evidence = `${summary.invalid} check${summary.invalid === 1 ? " still needs" : "s still need"} evidence or a responsible verifier.`;
    return summary.failed > 0 ? `${failed} ${evidence}` : evidence;
  }
  if (summary.unchecked > 0) {
    const unchecked = `${summary.unchecked} required check${summary.unchecked === 1 ? " has" : "s have"} not been completed.`;
    return summary.failed > 0 ? `${failed} ${unchecked}` : unchecked;
  }
  if (summary.failed > 0) return `All checks have evidence. ${failed}`;
  return "All required checks passed.";
}

function lifecycleNextAction(
  state: GovernanceState,
): GovernanceNextAction | null {
  const actions: Partial<Record<GovernanceState, GovernanceNextAction>> = {
    ready_for_delivery: {
      key: GOVERN_GUIDANCE_KEY.START_DELIVERY,
      action: "Start delivery",
      message: "Start delivery before verification checks can be recorded.",
    },
    in_delivery: {
      key: GOVERN_GUIDANCE_KEY.CONTINUE_DELIVERY,
      action: "Continue delivery",
      message: "Complete delivery, then mark the item Ready for review.",
    },
    needs_rework: {
      key: GOVERN_GUIDANCE_KEY.RESUME_DELIVERY,
      action: "Resume delivery",
      message: "Resume delivery to correct the failed or insufficient work.",
    },
    verified: {
      key: GOVERN_GUIDANCE_KEY.SCHEDULE_REVIEW,
      action: "Schedule next review",
      message: "Record when and why the result must be reviewed again.",
    },
    review_scheduled: {
      key: GOVERN_GUIDANCE_KEY.REVIEW_SCHEDULED,
      action: "Review scheduled",
      message: "The next review is scheduled.",
    },
  };
  return actions[state] ?? null;
}

function sourceAction(
  key: GovernSourceBlocker,
  message: string,
): GovernanceNextAction {
  return { key, action: "Open Brief", message };
}

function joinLabels(labels: string[]): string {
  if (labels.length < 2) return labels[0] ?? "required roles";
  if (labels.length === 2) return `${labels[0]} and ${labels[1]}`;
  return `${labels.slice(0, -1).join(", ")}, and ${labels.at(-1)}`;
}
